import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {isValidPhone} from 'utils/validators';

import {Button, Layout, ListItem, Select, TextArea, TextField} from '@phonectas/unifon-ui-kit';
import LanguageSelector from 'common/components/LanguageSelector';
import i18n from 'common/i18n';
import MessageLength from 'common/components/MessageLength';
import {CustomerUser} from 'common/types/store/threeSixtyTypes';

interface TemplateMessage {
    no: string;
    en: string;
}

interface Template {
    label: string;
    id: number;
    message: TemplateMessage;
}


const SmsModule: React.FC = () => {
	const [t] = useTranslation();
	const {sendSms} = useThreeSixtyNewStore((state) => ({sendSms: state.sendSms}), shallow);
	const {user} = useThreeSixtyNewStore((state) => ({
		user: state.activeRow as CustomerUser,
	}), shallow);
	const [text, setText] = useState<string>('');
	const [recipient, setRecipient] = useState('');
	const [language, setLanguage] = useState(i18n?.language || 'no');
	const [template, setTemplate] = useState<Template | null>(null);
	const templates = useMemo<Template[]>(() => {
		const safeUser: Partial<CustomerUser> = user ?? {};
		return [
			{
				label: 'pin/puk',
				id: 0,
				message: {
					no: `Hei ${safeUser.name || ''}, din Pin/Puk er ${safeUser.pin || ''} / ${safeUser.puk || ''}.`,
					en: `Hello ${safeUser.name || ''}, your Pin/Puk is ${safeUser.pin || ''} / ${safeUser.puk || ''}.`,
				},
			},
		];
	}, [user]);

	const handleResetSms = useCallback(() => {
		setText('');
		setRecipient('');
		setTemplate(null);
	}, []);
	const handleTextChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setText(e.target.value);
	}, []);
	const handleSetRecipient = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setRecipient(e.target.value);
	}, []);
	const onSendSms = useCallback(() => {
		sendSms({
			number: recipient,
			text: text,
			onSuccess: () => {
				handleResetSms();
			}
		});
	}, [sendSms, recipient, text, handleResetSms]);

	const onTemplateSelect = useCallback(
		(val: Template | null | undefined) => {
			if (val && val.id !== undefined) {
				setTemplate(val);
				const selectedTemplate = templates[val.id];
				if (selectedTemplate) {
					if (language === 'no') {
						setText(selectedTemplate.message.no);
					} else {
						setText(selectedTemplate.message.en);
					}
				}
			} else {
				setTemplate(null);
				setText('');
			}
		},
		[language, templates]
	);

	const handleLanguageChange = useCallback((lang: string) => {
		setLanguage(lang);
	}, []);

	useEffect(() => {
		if (template !== null) {
			const selectedTemplate = templates[template.id];
			if (selectedTemplate) {
				if (language === 'no') {
					setText(selectedTemplate.message.no);
				} else {
					setText(selectedTemplate.message.en);
				}
			}
		}
	}, [language, template, templates]);

	useEffect(() => {
		if (user && user?.phone_number) {
			setRecipient(user?.phone_number.toString());
		}
	}, [user]);

	const mobileValidator = useCallback((val: string | number) => {
		const strVal = typeof val === 'number' ? val.toString() : val;
		return !strVal || isValidPhone(strVal);
	}, []);

	return (
		<Layout direction="column" fullWidth nowrap fullHeight gap="sm" autoOverflow>
			<Layout direction="row" fullWidth>
				<LanguageSelector
					noAuto
					lang={language}
					onChange={handleLanguageChange}
				/>
			</Layout>
			<Layout direction="row" nowrap alignItems="end" fullWidth>
				<TextField
					fullWidth
					value={recipient}
					onChange={handleSetRecipient}
					label={t('Recipient')}
					validator={mobileValidator}
				/>
				<Select
					fullWidth
					label={t('Template')}
					onChange={(val) => {
						const selected = templates.find(t => t.id === val);
						onTemplateSelect(selected ?? null);
					}}
					value={template?.id ?? null}
					placeholder={t('Select template')}
				>
					{
						templates.map((temp) => {
							return (
								<ListItem
									key={temp.id}
									value={temp.id}
									primaryText={temp.label}
									selected={temp.id === template?.id}
								/>
							);
						})
					}
				</Select>
			</Layout>
			<MessageLength message={text} />
			<Layout direction="row" fullWidth>
				<TextArea
					fullWidth
					value={text}
					onChange={handleTextChange}
					placeholder={t('Text')}
					style={{height: '250px'}}
				/>
			</Layout>
			<Layout direction="row" nowrap justifyContent="end" fullWidth>
				<Button
					variant="secondary"
					color="error"
					onClick={handleResetSms}
				>
					{t('Clear')}
				</Button>
				<Button
					variant="primary"
					color="success"
					disabled={!text || !recipient}
					onClick={onSendSms}
				>
					{t('Send')}
				</Button>
			</Layout>
		</Layout>
	);
}
export default React.memo(SmsModule);
